

const DEV =
  typeof import.meta !== "undefined" &&
  import.meta.env?.DEV === true &&
  import.meta.env?.VITE_ENABLE_DEV_LOGGER !== "false";

const SENSITIVE_KEYS = [
  "token",
  "accessToken",
  "access_token",
  "refreshToken",
  "refresh_token",
  "id_token",
  "idToken",
  "authorization",
  "Authorization",
  "password",
  "secret",
  "apiKey",
  "api_key",
  "cookie",
  "Cookie",
  "Bearer",
  "credentials",
  "user_data",
  "userData",
  "session",
  "sessionId",
  "session_id",
  "auth",
  "private",
  "key",
  "csrf",
  "x-csrf-token",
  "set-cookie",
];

const TOKEN_LIKE_PATTERN =
  /^(Bearer\s+)?[A-Za-z0-9_-]{20,}(\.([A-Za-z0-9_-]+)){0,2}$|^[A-Za-z0-9+/]{40,}=*$/;

function isSensitiveKey(key) {
  if (typeof key !== "string") return false;
  const keyLower = key.toLowerCase();
  return SENSITIVE_KEYS.some(
    (s) => keyLower === s.toLowerCase() || keyLower.includes(s.toLowerCase())
  );
}

function redactString(value) {
  if (typeof value !== "string") return value;
  if (value.length < 20) return value;
  if (TOKEN_LIKE_PATTERN.test(value.trim())) return "[REDACTED]";
  if (/^Bearer\s+/i.test(value)) return "[REDACTED]";
  return value;
}

function redact(obj, depth = 0) {
  const MAX_DEPTH = 5;
  if (depth > MAX_DEPTH) return "[max depth]";
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return obj;
  if (Array.isArray(obj)) return obj.map((item) => redact(item, depth + 1));

  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      out[key] = "[REDACTED]";
    } else if (typeof value === "string") {
      out[key] = redactString(value);
    } else {
      out[key] = redact(value, depth + 1);
    }
  }
  return out;
}

function sanitize(args) {
  return args.map((arg) => {
    if (arg instanceof Error) {
      return {
        name: arg.name,
        message: redactString(arg.message) || "[Error]",
        code: arg.code,
      };
    }
    if (arg && typeof arg === "object" && !Array.isArray(arg) && !(arg instanceof Date)) {
      return redact(arg);
    }
    if (typeof arg === "string") return redactString(arg);
    return arg;
  });
}

function noop() {}

function bind(level) {
  if (!DEV) return noop;
  const fn = console[level] || console.log;
  return (...args) => fn.apply(console, sanitize(args));
}

export const logger = {
  log: bind("log"),
  warn: bind("warn"),
  error: bind("error"),
  info: bind("info"),
  debug: bind("debug"),
};

/**
 * Create a namespaced logger for easier filtering in dev tools.
 * @param {string} namespace - e.g. "invokeApi", "Auth"
 */
export function createLogger(namespace) {
  if (!DEV) {
    return { log: noop, warn: noop, error: noop, info: noop, debug: noop };
  }
  const prefix = `[${namespace}]`;
  return {
    log: (...args) => logger.log(prefix, ...args),
    warn: (...args) => logger.warn(prefix, ...args),
    error: (...args) => logger.error(prefix, ...args),
    info: (...args) => logger.info(prefix, ...args),
    debug: (...args) => logger.debug(prefix, ...args),
  };
}

export default logger;
